import { Button } from "@/overlay/components/CommonParts/buttons";
import { Section } from "@/overlay/components/LayerStackWindow/WeatherTableSettings/Section";
import { WeatherTableDataForm } from "@/overlay/components/LayerStackWindow/WeatherTableSettings/WeatherTableDataForm";
import { useAppDispatch } from "@/reducer";
import { updateWeatherTableState } from "@/reducer/WeatherTablesState";
import { classNames } from "@/utility/jsx";
import {
  type WeatherTable,
  type WeatherTableColumn,
  getWeatherTableColumnCustomLabel,
  getWeatherTableColumnLabel,
} from "@/weatherTableView/types";
import { t } from "@lingui/core/macro";
import { cloneDeep } from "lodash";
import { useCallback, useState } from "react";

interface StackProps {
  weatherTable: WeatherTable;
  onRemoveColumn: (groupIdx: number, column: WeatherTableColumn) => void;
}

interface ColumnsStackProps {
  weatherTable: WeatherTable;
  columns: WeatherTableColumn[];
  groupIdx: number;
  onRemoveParameter: (groupIdx: number, column: WeatherTableColumn) => void;
}

export function GroupsStack({ weatherTable, onRemoveColumn }: StackProps) {
  return (
    <>
      {weatherTable.groups.map((group, groupIdx) => (
        <ColumnsStack
          key={`group-${group.groupInfo.title}`}
          weatherTable={weatherTable}
          columns={group.columns}
          groupIdx={groupIdx}
          onRemoveParameter={onRemoveColumn}
        />
      ))}
    </>
  );
}

export function ColumnsStack({ weatherTable, onRemoveParameter, columns, groupIdx }: ColumnsStackProps) {
  const dispatch = useAppDispatch();

  const handleWeatherTableChange = useCallback(
    (weatherTable: WeatherTable) => {
      if (weatherTable) {
        dispatch(updateWeatherTableState({ weatherTable, id: weatherTable.id }));
      }
    },
    [dispatch],
  );

  const [columnIds] = useState(() => columns.map((_, idx) => `${groupIdx}-${idx}`));

  const handleColumnLabelChange = useCallback(
    (columnIdx: number, label: string | null) => {
      const updatedWeatherTable = cloneDeep(weatherTable);
      updatedWeatherTable.groups[groupIdx].columns[columnIdx] = {
        ...updatedWeatherTable.groups[groupIdx].columns[columnIdx],
        title: label ?? "",
      };
      handleWeatherTableChange(updatedWeatherTable);
    },
    [groupIdx, handleWeatherTableChange, weatherTable],
  );

  return (
    <div className={classNames("expansion-panel", "box-section--breakout")}>
      {columns.map((column: WeatherTableColumn, columnIdx: number) => (
        <Section
          key={columnIds[columnIdx]}
          tag={t`column`}
          title={getWeatherTableColumnLabel(column)}
          editableTitle={{
            defaultLabel: column.parameterUnit,
            label: getWeatherTableColumnCustomLabel(column),
            onSave: (label) => handleColumnLabelChange(columnIdx, label),
          }}
          actionButton={
            <>
              <Button onClick={() => onRemoveParameter(groupIdx, column)} icon="delete" />
            </>
          }
        >
          <WeatherTableDataForm
            onChange={handleWeatherTableChange}
            weatherTable={weatherTable}
            groupIdx={groupIdx}
            columnIdx={columnIdx}
          />
        </Section>
      ))}
    </div>
  );
}
